import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Store } from '@ngxs/store'
import { map, Observable, Subject } from 'rxjs'
import { environment } from 'src/environments/environment'
import { IFiles } from 'src/interfaces/files/files.interface'
import { IPagination } from 'src/interfaces/pagination/pagination.interface'
import { IProposalStatus } from 'src/interfaces/proposal/proposal.interface'
import { UserMetaState } from 'src/store/user/user.meta.state'
import { FilesUtils } from './../../../helpers/files.helper'
import { RankedLocations } from 'src/interfaces/chart/chart.interface'
import { Order } from 'src/interfaces/order/order.interface'
import { ControlParams } from 'src/helpers/utils.helper'

export enum PeriodTypes {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
  CUSTOM = 'CUSTOM',
}

export enum ChartHomeTypes {
  SALES = 'SALES',
  CONVERSION_RATES = 'CONVERSION_RATES',
}

export enum ChartDataType {
  VALUE = 'VALUE',
  POTENCY = 'POTENCY',
}

export enum ReportTypes {
  PROPOSAL = 'PROPOSAL',
  PURCHASE_ORDER = 'PURCHASE_ORDER',
  INVOICE = 'INVOICE',
  DELIVERED = 'DELIVERED',
}

interface IGenerateBoletoPayload {
  address: string
  neighborhood: string
  city: string
  uf: string
  zip_code: number
  document_type: number
  doc: number
  name: string
  expiration_date: number
  emission_date: number
  value: string
  pix_key_type: number
  pix_key_value: string
  message: string
  pix_id: string
  order_number: string
}

export interface GenerateBoletoDto {
  address: string
  neighborhood: string
  city: string
  uf: string
  zip_code: number
  document_type: number
  doc: number
  name: string
  expiration_date: number
  emission_date: number
  value: string
  message: string
  order_number: string
}

export interface MapSeries {
  type: string
  features: any[]
}

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  baseRoute = `${environment.api}/order`
  proposalStatus: IProposalStatus[] = [
    {
      title: 'Created',
      value: 'CREATED',
      class: 'gold',
    },
    {
      title: 'In Approval',
      value: 'IN_APPROVAL',
      class: 'orange',
    },
    {
      title: 'In Processing',
      value: 'IN_PROCESSING',
      class: 'orange',
    },
    {
      title: 'Awaiting Payment',
      value: 'AWAITING_PAYMENT',
      class: 'orange',
    },
    {
      title: 'Payment Approved',
      value: 'PAYMENT_APPROVED',
      class: 'green',
    },
    {
      title: 'Financing Approved',
      value: 'FINANCING_APPROVED',
      class: 'green',
    },
    {
      title: 'In Assemble',
      value: 'IN_ASSEMBLE',
      class: 'blue',
    },
    {
      title: 'Invoice',
      value: 'INVOICE',
      class: 'blue',
    },
    {
      title: 'In Delivery',
      value: 'IN_DELIVERY',
      class: 'pink',
    },
    {
      title: 'Delivered',
      value: 'DELIVERED',
      class: 'green',
    },
    {
      title: 'Loss',
      value: 'LOSS',
      class: 'red',
    },
  ]

  private rankedLocationsSubject = new Subject<RankedLocations[]>()
  public rankedLocations$: Observable<RankedLocations[]> =
    this.rankedLocationsSubject.asObservable()

  private orderDataToPdfSubject = new Subject<Order>()
  public orderDataToPdf$: Observable<Order> =
    this.orderDataToPdfSubject.asObservable()

  private resetPreCheckoutInputsSubject = new Subject<void>()

  constructor(
    private http: HttpClient,
    private store: Store,
    private filesUtils: FilesUtils,
  ) {}

  list(filterData: IPagination): Observable<any> {
    return this.http.get(`${this.baseRoute}`, {
      params: { ...filterData },
    })
  }

  listAll(params?: IPagination): Observable<any> {
    return this.http.get(`${this.baseRoute}/list-all`, {
      params: { ...params },
    })
  }

  createQuotation(data: any) {
    return this.http.post(`${this.baseRoute}/create-proposal`, data)
  }

  getById(id: string): Observable<any> {
    return this.http.get(`${this.baseRoute}/id/${id}`)
  }

  getByOrderNumber(orderNumber: string): Observable<any> {
    return this.http.get(`${this.baseRoute}/order-number/${orderNumber}`)
  }

  edit(id: string, data: any): Observable<any> {
    return this.http.patch(`${environment.api}/order/update/${id}`, data)
  }

  delete(id: string): Observable<any> {
    return this.http.patch(`${this.baseRoute}/delete/${id}`, {})
  }

  sendMessage(
    data: { description: string; content?: string },
    orderId: string,
  ): Observable<any> {
    return this.http.post(`${this.baseRoute}/send-message/${orderId}`, data)
  }

  updatePaymentStatus(
    data: { paymentStatus: string },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(
      `${this.baseRoute}/update-payment-status/${orderId}`,
      data,
    )
  }

  updateInstalationStatus(
    data: { installationStatus: string },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(
      `${this.baseRoute}/update-installation-status/${orderId}`,
      data,
    )
  }

  updateDeliveryStatus(
    data: { deliveryStatus: string },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(
      `${this.baseRoute}/update-delivery-status/${orderId}`,
      data,
    )
  }

  updateOrderStatus(
    data: { status: string },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(`${this.baseRoute}/update-status/${orderId}`, data)
  }

  updateOrderInternalStatus(
    data: { internalStatus: string },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(
      `${this.baseRoute}/update-internal-status/${orderId}`,
      data,
    )
  }

  updatePaymentMethod(
    data: { paymentMethod: string },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(
      `${this.baseRoute}/update-payment-method/${orderId}`,
      data,
    )
  }

  listCreditByOwner(): Observable<any> {
    const user = this.store.selectSnapshot(UserMetaState.getUser)
    return this.http.get(`${environment.api}/credit/list-by-owner/${user.id}`)
  }

  listOrderByPeriod(params: {
    period: PeriodTypes
    reportType: ReportTypes
  }): Observable<any> {
    return this.http.get(`${this.baseRoute}/list-by-period`, {
      params,
    })
  }

  getDailyReport(): Observable<any> {
    return this.http.get(`${this.baseRoute}/daily-report`)
  }

  getReportsHeatMap(): Observable<any> {
    return this.http.get(`${this.baseRoute}/reports-heat-map`)
  }

  getReportsValues(filterData: ControlParams): Observable<any> {
    return this.http.get(`${this.baseRoute}/reports-values`, {
      params: { ...filterData },
    })
  }

  uploadFiles(files: IFiles[], orderId: string): Observable<any> {
    const formData = this.filesUtils.createFileFormData(files)
    return this.http.post(
      `${environment.api}/storage/upload/orders/${orderId}`,
      formData,
    )
  }

  listFiles(orderId: string): Observable<any> {
    return this.http.get(`${this.baseRoute}/list-files/${orderId}`)
  }

  deleteFiles(orderId: string, files: any[]): Observable<any> {
    return this.http.post(
      `${environment.api}/storage/delete/orders/${orderId}`,
      { files },
    )
  }

  updateBYDNumber(
    data: { bydNumber: string },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(
      `${this.baseRoute}/update-bydNumber/${orderId}`,
      data,
    )
  }

  updateInvoiceNumber(
    data: { customInvoiceCode: string },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(
      `${this.baseRoute}/${orderId}/customInvoiceNumber`,
      data,
    )
  }

  updateReference(
    data: { reference: string },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(
      `${this.baseRoute}/update-reference/${orderId}`,
      data,
    )
  }

  overview(params: any): Observable<any> {
    return this.http.get(`${this.baseRoute}/overview`, {
      params: { ...params },
    })
  }

  generateBoleto(invoiceId: string, dueDate: string): Observable<Blob> {
    return this.http
      .get(`${environment.api}/invoices/generate-billet/${invoiceId}`, {
        params: { dueDate },
        responseType: 'blob',
      })
      .pipe(
        map((response: Blob) => {
          return response
        }),
      )
  }

  searchOrdersByQuery(query: string): Observable<any> {
    return this.http.get(`${this.baseRoute}/search-orders`, {
      params: { query },
    })
  }

  getGeojson(id) {
    return this.http.get<MapSeries>(`assets/geojson/${id}.json`)
  }

  updateLinkedIntegrator(
    data: {
      id: string
      displayName: string
      document: string
    },
    orderId: string,
  ): Observable<any> {
    return this.http.patch(`${this.baseRoute}/integrator/${orderId}`, data)
  }

  setRankedLocations(rankedLocations: RankedLocations[]) {
    this.rankedLocationsSubject.next(rankedLocations)
  }

  setOrderDataToPdf(order: Order) {
    this.orderDataToPdfSubject.next(order)
  }

  clearOrderDataToPdf() {
    this.orderDataToPdfSubject.next(null)
  }

  resetPreCheckoutInputs() {
    this.resetPreCheckoutInputsSubject.next()
  }

  getPreCheckoutInputsObservable() {
    return this.resetPreCheckoutInputsSubject.asObservable()
  }

  getOrderPdf(orderId: string) {
    return this.http.get<{ downloadUrl: string }>(
      `${this.baseRoute}/pdf/${orderId}`,
    )
  }
}
